import { ReactNode, useRef } from 'react'
import { CloseCircleFilled } from '@ant-design/icons'
import { Button, Flex } from 'antd'
import { CSSTransition } from 'react-transition-group'
import cn from 'classnames'
import s from './s.module.css'

interface Props {
  right?: ReactNode
  header: ReactNode
  children: ReactNode
  onOpen?: () => void
  onClose?: () => void
  isOpen: boolean
  withHoverBorder?: boolean
  withCloseButton?: boolean
  isDisabled?: boolean
  isCursorPointer?: boolean
}

export default function Collapse({
  children,
  right,
  header,
  isOpen,
  onOpen,
  onClose,
  withHoverBorder,
  withCloseButton,
  isDisabled,
  isCursorPointer,
}: Props) {
  const nodeRef = useRef(null)

  const handleOpen = () => {
    if (!isOpen && !isDisabled) {
      onOpen?.()
    }
  }

  const shouldRenderRight = right || (withCloseButton && isOpen)

  return (
    <div
      className={cn(s.wrap, {
        [s.border]: withHoverBorder,
        [s.open]: isOpen,
        [s.cursorPointer]: isCursorPointer,
      })}
      onClick={handleOpen}
    >
      <Flex align="center" className={cn(s.control, { [s.withCloseButton]: withCloseButton })}>
        {header}
        {shouldRenderRight && (
          <Flex gap={16} className={s.right}>
            {right ?? null}
            {withCloseButton && (
              <Button
                size="small"
                icon={<CloseCircleFilled />}
                type="link"
                onClick={onClose}
                className={s.closeButton}
              />
            )}
          </Flex>
        )}
      </Flex>
      <CSSTransition
        in={isOpen}
        timeout={300}
        classNames={{
          enter: s.enter,
          enterActive: s.enterActive,
          exit: s.exit,
          exitActive: s.exitActive,
        }}
        unmountOnExit
        nodeRef={nodeRef}
      >
        <div ref={nodeRef}>
          <div className={s.content}>{children}</div>
        </div>
      </CSSTransition>
    </div>
  )
}
